import axios from "axios"

import Analytics from "./analytics"

export const getCookie = (name) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  return parts.length === 2 ? parts.pop().split(";").shift() : null
}

export const setCookie = (name, value) => {
  const expires = new Date()
  expires.setMonth(expires.getMonth() + 6)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;domain=${window.location.hostname.includes(".") ? `.${window.location.hostname.split(".").slice(-2).join(".")}` : window.location.hostname};SameSite=Lax`
}

export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname.includes(".") ? `.${window.location.hostname.split(".").slice(-2).join(".")}` : window.location.hostname};SameSite=Lax`
}

export const authRequestOptions = () => ({
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
    "X-CSRFToken": getCookie("csrftoken"),
  },
  withCredentials: true,
})

const errorReturn = (r) => {
  if (typeof r.response?.data === "object")
    return Object.keys(r.response.data).reduce(
      (a, b) => a.concat(r.response.data[b]),
      []
    )[0]
  return r.response?.data
}

export const handleCsrf = async () =>
  axios
    .get(`${process.env.GATSBY_APP_ENGINE_BASE_URL}/api/csrf/`, {
      withCredentials: true,
    })
    .catch(() => false)

export const handleLogin = async (email, password) =>
  axios
    .post(
      `${process.env.GATSBY_APP_ENGINE_BASE_URL}/api/login/`,
      {
        email,
        password,
      },
      authRequestOptions()
    )
    .then(() => {
      Analytics.triggerEvent("login_success", {}, "LoginSuccess")
      setCookie("isAuthenticated", "true")
      return true
    })
    .catch((r) => {
      Analytics.triggerEvent(
        "login_error",
        { error_code: r.response?.status },
        "LoginError"
      )
      return errorReturn(r)
    })

export const handleCreateAccount = async (
  email,
  password1,
  password2,
  firstName,
  lastName,
  country,
  cmId,
  customFields
) =>
  axios
    .post(
      `${process.env.GATSBY_APP_ENGINE_BASE_URL}/api/register/`,
      {
        email,
        password1,
        password2,
        first_name: firstName,
        last_name: lastName,
        mailing_list_ids: [cmId],
        mailing_list_data: customFields,
        country_code: country || process.env.GATSBY_COUNTRY,
      },
      authRequestOptions()
    )
    .then(() => {
      Analytics.triggerEvent(
        "create_account_success",
        {},
        "CreateAccountSuccess"
      )
      setCookie("isAuthenticated", "true")
      return true
    })
    .catch((r) => {
      Analytics.triggerEvent(
        "create_account_error",
        { error_code: r.response?.status },
        "CreateAccountError"
      )
      return errorReturn(r)
    })

export const handleCreateAnonymousAccount = async (
  email,
  country,
  cmId,
  customFields
) =>
  axios
    .post(
      `${process.env.GATSBY_APP_ENGINE_BASE_URL}/api/register/anonymous/`,
      {
        email,
        mailing_list_ids: [cmId],
        mailing_list_data: customFields,
        country_code: country || process.env.GATSBY_COUNTRY,
      },
      authRequestOptions()
    )
    .then(() => {
      Analytics.triggerEvent(
        "create_account_success",
        {},
        "CreateAccountSuccess"
      )
      setCookie("isAuthenticated", "true")
      return true
    })
    .catch((r) => {
      Analytics.triggerEvent(
        "create_account_error",
        { error_code: r.response?.status },
        "CreateAccountError"
      )
      return errorReturn(r)
    })

export const handleUpdateAccount = async (
  email,
  password1,
  password2,
  firstName,
  lastName,
  country,
  phone,
  zipcode
) =>
  axios
    .put(
      `${process.env.GATSBY_APP_ENGINE_BASE_URL}/api/user/`,
      password1 || password2
        ? {
            email: email || undefined,
            password1,
            password2,
            first_name: firstName || undefined,
            last_name: lastName || undefined,
            country_code: country || undefined,
            phone_number: phone || undefined,
            postal_zip_code: zipcode || undefined,
          }
        : {
            email: email || undefined,
            first_name: firstName || undefined,
            last_name: lastName || undefined,
            country_code: country || undefined,
            phone_number: phone || undefined,
            postal_zip_code: zipcode || undefined,
          },
      authRequestOptions()
    )
    .then(() => true)
    .catch((r) => errorReturn(r))

export const handleResetPassword = async (email, url) =>
  axios
    .post(
      `${process.env.GATSBY_APP_ENGINE_BASE_URL}/api/password/reset/`,
      {
        email,
        frontend_reset_url: `${url}/account/confirm-password/`,
      },
      authRequestOptions()
    )
    .then(() => true)
    .catch((r) => errorReturn(r))

export const handleConfirmPassword = async (
  newPassword1,
  newPassword2,
  uid,
  token
) =>
  axios
    .post(
      `${process.env.GATSBY_APP_ENGINE_BASE_URL}/api/password/reset/confirm/`,
      {
        new_password1: newPassword1,
        new_password2: newPassword2,
        uid,
        token,
      },
      authRequestOptions()
    )
    .then(() => true)
    .catch((r) => errorReturn(r))

export const isLoggedIn = () =>
  (getCookie("isAuthenticated") || localStorage.getItem("isAuthenticated")) &&
  getCookie("csrftoken")

export const logout = (callback) =>
  axios
    .post(
      `${process.env.GATSBY_APP_ENGINE_BASE_URL}/api/logout/`,
      {},
      authRequestOptions()
    )
    .then(() => {
      deleteCookie("isAuthenticated")
      localStorage.removeItem("isAuthenticated")
      callback()
    })
    .catch((r) => errorReturn(r))

export const getUser = (callback) =>
  isLoggedIn()
    ? axios
        .get(
          `${process.env.GATSBY_APP_ENGINE_BASE_URL}/api/user/`,
          authRequestOptions()
        )
        .then((response) => callback(response.data))
        .catch(() => {
          deleteCookie("isAuthenticated")
          localStorage.removeItem("isAuthenticated")
          window.location.reload()
        })
    : () => {
        deleteCookie("isAuthenticated")
        localStorage.removeItem("isAuthenticated")
        window.location.reload()
      }
